import { BOOKING_ENTRY_HASH, BOOKING_PANEL_PATHS, bookingEntryCohortKey, bookingEntryUrl } from './bookingEntry.js'

const ANALYTICS_ENDPOINT = '/analytics/events'
const TRAINING_PROPERTY_VALUES = {
  training: ['bali', 'portugal'],
  campaign: ['portugal-march-2027-dates'],
  utm_source: ['bento'],
  utm_medium: ['email'],
}
const TRAINING_PROPERTIES = Object.keys(TRAINING_PROPERTY_VALUES)
const TRAINING_PATHS = {
  '/program': 'bali',
  '/bali-program': 'bali',
  '/portugal-pilates-teacher-training': 'portugal',
}

const BROWSER_EVENT_PROPERTIES = {
  page_view: ['component', 'partner_code', 'cohort_key', ...TRAINING_PROPERTIES],
  training_program_view: TRAINING_PROPERTIES,
  program_guide_view: ['partner_code'],
  program_guide_download_click: ['location'],
  contact_message_open: ['location'],
  reserve_spot_click: ['location', 'cohort_key', ...TRAINING_PROPERTIES],
  booking_visit: ['partner_code', 'cohort_key', ...TRAINING_PROPERTIES],
}

let currentCsrfToken = null
let analyticsEnabled = false
let marketingEnabled = false
let installation = null

export function trackingPermissionsFor(page) {
  const consent = page?.props?.cookieConsent || {}
  const trackingAllowed = page?.props?.trackingAllowed === true
  // An explicit first-party gate must never fall back to the advertising gate.
  const analyticsAllowed = Object.hasOwn(page?.props || {}, 'analyticsAllowed')
    ? page.props.analyticsAllowed === true
    : trackingAllowed

  return {
    analytics: analyticsAllowed && consent.analytics === true,
    marketing: trackingAllowed && consent.marketing === true,
  }
}

export function browserEventProperties(name, properties = {}) {
  const allowedKeys = BROWSER_EVENT_PROPERTIES[name] || []

  return allowedKeys.reduce((cleaned, key) => {
    const value = properties[key]
    if (value === undefined || value === null || value === '') return cleaned
    if (key === 'cohort_key' && !validCohortKey(value)) return cleaned
    if (TRAINING_PROPERTY_VALUES[key] && !TRAINING_PROPERTY_VALUES[key].includes(value)) return cleaned

    cleaned[key] = value
    return cleaned
  }, {})
}

export function trackEvent(name, properties = {}) {
  if (!analyticsEnabled || !BROWSER_EVENT_PROPERTIES[name] || typeof window === 'undefined') return

  const token = currentCsrfToken || document.querySelector('meta[name="csrf-token"]')?.content
  if (!token) return

  window.fetch(ANALYTICS_ENDPOINT, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
    },
    credentials: 'same-origin',
    keepalive: true,
    body: JSON.stringify({
      analytics_event: {
        name,
        path: window.location.pathname,
        properties: browserEventProperties(name, properties),
      },
    }),
  }).catch(() => {})
  return true
}

// A training page's booking panel (UI-20261020-003) is the funnel's booking
// visit, like a load of /booking. Callers record it once per page visit.
export function trackBookingPanelVisit({ partnerCode = '', cohortKey = '' } = {}) {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (!BOOKING_PANEL_PATHS.includes(url.pathname)) return
  return trackEvent('booking_visit', {
    partner_code: normalizePartnerCode(partnerCode) || normalizePartnerCode(url.searchParams.get('ref')),
    cohort_key: cohortKey,
    ...trainingProperties(url),
  })
}

export function installAnalytics(router, initialPage) {
  if (installation?.document === document) {
    installation.trackPage(initialPage)
    return installation.dispose
  }

  let lastPageUrl = null
  let analyticsPageRecorded = false
  let marketingPageRecorded = false
  let initialPageVisit = true
  let currentPage = initialPage
  const hasInitialMetaPageView = window.rootedMetaPixelLoaded === true

  trackPage(initialPage)

  const unsubscribe = router.on('navigate', event => trackPage(event.detail.page))

  // Delegation covers lazy content and the teleported navigation drawer.
  // One handler gives annotations priority, avoiding double-counted anchors.
  const trackClick = event => {
    if (!analyticsEnabled || !(event.target instanceof Element)) return

    const target = event.target.closest('[data-analytics-event], a[href]')
    if (!target) return

    const currentUrl = new URL(window.location.href)
    const bookingUrl = publicBookingUrl(target.getAttribute('href'), currentUrl)
    const name = target.dataset.analyticsEvent || (bookingUrl ? 'reserve_spot_click' : null)
    if (!name) return
    const annotatedTraining = TRAINING_PROPERTY_VALUES.training.includes(target.dataset.analyticsTraining)
      ? target.dataset.analyticsTraining : undefined

    trackEvent(name, {
      location: target.dataset.analyticsLocation,
      cohort_key: name === 'reserve_spot_click' ? bookingCohortKey(bookingUrl) : undefined,
      ...trainingProperties(currentUrl, currentPage),
      training: annotatedTraining || trainingForUrl(bookingUrl) || trainingForUrl(currentUrl, currentPage),
    })
  }
  document.addEventListener('click', trackClick)

  const installedDocument = document
  const dispose = () => {
    unsubscribe?.()
    installedDocument.removeEventListener('click', trackClick)
    if (installation?.dispose === dispose) {
      installation = null
      analyticsEnabled = false
      marketingEnabled = false
      currentCsrfToken = null
    }
  }
  installation = { document, trackPage, dispose }
  return dispose

  function trackPage(page) {
    currentPage = page
    currentCsrfToken = page?.props?.csrfToken || currentCsrfToken

    const permissions = trackingPermissionsFor(page)
    analyticsEnabled = permissions.analytics
    marketingEnabled = permissions.marketing

    const url = new URL(page?.url || window.location.href, window.location.origin)
    // Hash-only scrolling is not a new page. Consent updates can record this
    // visit for the first time after opting in, without recording it twice.
    const pageUrl = `${url.pathname}${url.search}`
    if (pageUrl !== lastPageUrl) {
      initialPageVisit = lastPageUrl === null
      lastPageUrl = pageUrl
      analyticsPageRecorded = false
      marketingPageRecorded = false
    }
    const path = url.pathname
    const partnerCode = normalizePartnerCode(url.searchParams.get('ref'))
    const training = { ...trainingProperties(url, page), cohort_key: bookingCohortKey(url, page) }
    const pageProperties = {
      component: page?.component,
      partner_code: partnerCode,
      ...training,
    }

    if (!analyticsPageRecorded && trackEvent('page_view', pageProperties)) {
      analyticsPageRecorded = true
      if (TRAINING_PATHS[path]) trackEvent('training_program_view', training)
      if (path === '/booking') trackEvent('booking_visit', { partner_code: partnerCode, ...training })
      if (path === '/program-guide') trackEvent('program_guide_view', { partner_code: partnerCode })
    }

    if (!marketingPageRecorded && marketingEnabled && typeof window.fbq === 'function') {
      trackMetaPage(path, pageProperties, { skipPageView: initialPageVisit && hasInitialMetaPageView })
      marketingPageRecorded = true
    }
  }
}

// Lookup-only context: the server resolves a real Cohort and never stores this key.
function validCohortKey(value) {
  return typeof value === 'string' && value.length >= 1 && value.length <= 64 && !/[^a-z0-9-]/.test(value)
}

function bookingCohortKey(url, page) {
  if (url && url.hash === BOOKING_ENTRY_HASH && BOOKING_PANEL_PATHS.includes(url.pathname)) {
    return bookingEntryCohortKey(url) || undefined
  }
  if (url?.pathname !== '/booking') return undefined
  const value = url.searchParams.get('cohort') || page?.props?.initialCohortKey
  return validCohortKey(value) ? value : undefined
}

function trainingForUrl(url, page) {
  if (!url) return undefined
  if (TRAINING_PATHS[url.pathname]) return TRAINING_PATHS[url.pathname]
  if (url.pathname !== '/booking') return undefined

  const training = page?.props?.training?.id
  if (TRAINING_PROPERTY_VALUES.training.includes(training)) return training
  // The location enum is descriptive; attribution uses server-resolved cohort context.
  return /^(bali|portugal)-[a-z0-9-]{1,64}$/.exec(url.searchParams.get('cohort') || '')?.[1]
}

function trainingProperties(url, page) {
  return browserEventProperties('training_program_view', {
    training: trainingForUrl(url, page),
    campaign: url.searchParams.get('utm_campaign'),
    utm_source: url.searchParams.get('utm_source'),
    utm_medium: url.searchParams.get('utm_medium'),
  })
}

function publicBookingUrl(href, currentUrl) {
  if (!href) return null
  try {
    const url = new URL(href, currentUrl)
    if (url.origin !== currentUrl.origin) return null
    // The standalone page, or a training page's booking-panel entry (`#book`).
    return url.pathname === '/booking' || bookingEntryUrl(url.href, currentUrl.href) ? url : null
  } catch {
    return null
  }
}

function trackMetaPage(path, properties = {}, { skipPageView = false } = {}) {
  if (!marketingEnabled || typeof window === 'undefined' || typeof window.fbq !== 'function') return

  if (!skipPageView) window.fbq('track', 'PageView')

  const viewContent = metaViewContentFor(path, properties)
  if (viewContent) window.fbq('track', 'ViewContent', viewContent)
}

export function metaViewContentFor(path, properties = {}) {
  const contentByPath = {
    '/program': 'Bali Pilates Teacher Training Program',
    '/booking': 'Rooted Movement Booking',
    '/program-guide': 'Rooted Movement Program Guide',
  }

  const contentName = contentByPath[path]
  if (!contentName) return null

  return compactProperties({
    content_name: contentName,
    content_category: 'Pilates teacher training',
    page_path: path,
    partner_code: properties.partner_code,
  })
}

export function normalizePartnerCode(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^A-Z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function compactProperties(properties) {
  return Object.entries(properties || {}).reduce((cleaned, [key, value]) => {
    if (value === undefined || value === null || value === '') return cleaned

    cleaned[key] = value
    return cleaned
  }, {})
}
